/**
 * Minimal client-side router.
 *
 * A dependency-free history router keeps the bundle small and the behaviour
 * predictable: no nested layout magic, no route-level data loading, and the
 * browser history remains the single source of truth.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AnchorHTMLAttributes, ReactNode } from 'react';

export interface RouteLocation {
  path: string;
  query: URLSearchParams;
  hash: string;
}

export interface RouterContextValue extends RouteLocation {
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterContextValue | null>(null);

function readLocation(): RouteLocation {
  const { pathname, search, hash } = window.location;
  return {
    path: pathname.replace(/\/+$/, '') || '/',
    query: new URLSearchParams(search),
    hash,
  };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<RouteLocation>(() => readLocation());

  useEffect(() => {
    const onPopState = () => setLocation(readLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    const target = to.startsWith('/') ? to : `/${to}`;
    if (options?.replace) window.history.replaceState(null, '', target);
    else window.history.pushState(null, '', target);
    setLocation(readLocation());
    // Keep focus predictable for keyboard and screen-reader users.
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const value = useMemo<RouterContextValue>(
    () => ({ ...location, navigate }),
    [location, navigate],
  );
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterContextValue {
  const context = useContext(RouterContext);
  if (!context) throw new Error('useRouter must be used inside RouterProvider');
  return context;
}

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  children: ReactNode;
}

export function Link({ to, children, onClick, ...rest }: LinkProps) {
  const { navigate } = useRouter();
  return (
    <a
      href={to}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

/* -------------------------------------------------------------------------- */
/* Route matching                                                             */
/* -------------------------------------------------------------------------- */

export interface RouteDefinition {
  pattern: string;
  element: ReactNode;
}

export interface RouteResolution {
  element: ReactNode;
  params: Record<string, string>;
  matchedPattern: string | null;
}

function matchPattern(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i += 1) {
    const expected = patternParts[i]!;
    const actual = pathParts[i]!;
    if (expected.startsWith(':')) {
      params[expected.slice(1)] = decodeURIComponent(actual);
    } else if (expected !== actual) {
      return null;
    }
  }
  return params;
}

export function resolveRoute(routes: RouteDefinition[], path: string): RouteResolution {
  for (const route of routes) {
    const params = matchPattern(route.pattern, path);
    if (params) return { element: route.element, params, matchedPattern: route.pattern };
  }
  return { element: null, params: {}, matchedPattern: null };
}
